import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboard } from '../api/educentialApi';
import {
  Home,
  Globe,
  Briefcase,
  BookOpen,
  FileText,
  Award,
  DollarSign,
  Building,
  Plane,
  MapPin,
  Users,
  User,
  Bell,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertTriangle,
  Upload,
  Eye,
  TrendingUp,
  Target,
  Mail,
  Grid3x3,
  Shield
} from 'lucide-react';

const SERVICES = [
  { id: 'overseas', name: 'Overseas Education', icon: Globe, active: true },
  { id: 'career', name: 'Career Counseling', icon: Briefcase, active: false },
  { id: 'skills', name: 'Skill Development', icon: BookOpen, active: false },
  { id: 'test', name: 'Test Preparation', icon: FileText, active: false },
  { id: 'scholarship', name: 'Scholarship Finder', icon: Award, active: false },
  { id: 'visa', name: 'Visa Assistance', icon: DollarSign, active: false },
  { id: 'accommodation', name: 'Accommodation', icon: Building, active: false },
  { id: 'predeparture', name: 'Pre-Departure', icon: Plane, active: false },
  { id: 'postarrival', name: 'Post-Arrival', icon: MapPin, active: false },
  { id: 'alumni', name: 'Alumni Network', icon: Users, active: false },
];

const OVERSEAS_NAV = [
  { name: 'Overview', path: '/dashboard', icon: Home },
  { name: 'UK Pathfinder', path: '/dashboard/uk/pathfinder', icon: Globe },
  { name: 'Financial Analysis', path: '/dashboard/uk/financial-analysis', icon: DollarSign },
  { name: 'Readiness Assessment', path: '/dashboard/uk/readiness-assessment', icon: CheckCircle },
  { name: 'Application', path: '/dashboard/application', icon: Upload },
  { name: 'Progress', path: '/dashboard/progress', icon: TrendingUp },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [serviceSwitcherOpen, setServiceSwitcherOpen] = useState(false);
  const [currentService, setCurrentService] = useState('overseas');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const dashboardData = await getDashboard();
      setData(dashboardData);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(err.response?.data?.error || 'Unable to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSwitch = (serviceId) => {
    const service = SERVICES.find(s => s.id === serviceId);
    if (service && service.active) {
      setCurrentService(serviceId);
      setServiceSwitcherOpen(false);
      // Navigate to service home
      if (serviceId === 'overseas') {
        navigate('/dashboard');
      }
    }
  };

  const { fullName, email, currentStage, documents, applicationStages, nextAction } = data || {};

  const stageDisplay = typeof currentStage === 'object'
    ? currentStage?.title || `Stage ${currentStage?.index || 1} of ${currentStage?.total || 8}`
    : currentStage || 'Getting Started';

  const actionDisplay = typeof nextAction === 'object'
    ? nextAction?.title || nextAction?.status || 'Continue your application'
    : nextAction || 'Continue your application';

  const completedStages = applicationStages?.filter(s => s.status === 'Completed').length || 0;
  const totalStages = applicationStages?.length || 8;
  const progressPercent = Math.round((completedStages / totalStages) * 100);

  const getDocumentStats = () => {
    if (!documents || documents.length === 0) return { uploaded: 0, total: 0 };
    const uploaded = documents.filter(d => d.status === 'Uploaded').length;
    return { uploaded, total: documents.length };
  };

  const docStats = getDocumentStats();
  const activeService = SERVICES.find(s => s.id === currentService);
  const ActiveServiceIcon = activeService?.icon || Globe;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Current Service Navigation Only */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Logo - Fixed */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">E</span>
              </div>
              <span className="font-bold text-gray-900">Educential</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Current Service Badge - Fixed */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center space-x-2 text-sm">
              <ActiveServiceIcon className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-700">{activeService?.name}</span>
            </div>
          </div>

          {/* Navigation for Current Service - Scrollable */}
          <nav className="flex-1 overflow-y-auto min-h-0">
            <div className="px-3 py-4 space-y-0.5">
              {OVERSEAS_NAV.map((item) => {
                const Icon = item.icon;
                const isActive = window.location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Bottom Section - Fixed (Help & Profile) */}
          <div className="border-t border-gray-200 flex-shrink-0">
            {/* Help Section */}
            <div className="px-3 py-3">
              <a
                href="mailto:info.educential@gmail.com"
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>Get Help</span>
              </a>
            </div>

            {/* Profile Section */}
            <div className="px-3 pb-4 pt-2 border-t border-gray-200">
              <div className="relative group">
                <button className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fullName || 'Student'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{email || user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>

                {/* Dropdown Menu */}
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 hidden group-hover:block">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Shield className="w-4 h-4 text-gray-500" />
                    <span>Settings</span>
                  </Link>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={logout}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">→</span>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Navbar */}
        <header className="h-16 bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4 lg:px-6">
            {/* Left Side */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700 p-2"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-bold text-gray-900 hidden md:block">Dashboard</h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-3">
              {/* Service Switcher */}
              <div className="relative">
                <button
                  onClick={() => setServiceSwitcherOpen(!serviceSwitcherOpen)}
                  className="flex items-center space-x-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                >
                  <Grid3x3 className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700 hidden sm:inline">Services</span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>

                {serviceSwitcherOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setServiceSwitcherOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Switch Service</p>
                      </div>
                      <div className="max-h-96 overflow-y-auto py-2">
                        {SERVICES.map((service) => {
                          const Icon = service.icon;
                          const isActive = service.id === currentService;

                          return (
                            <button
                              key={service.id}
                              onClick={() => handleServiceSwitch(service.id)}
                              disabled={!service.active}
                              className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                                isActive
                                  ? 'bg-yellow-50 border-l-4 border-yellow-500'
                                  : service.active
                                  ? 'hover:bg-gray-50'
                                  : 'opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  isActive
                                    ? 'bg-yellow-100'
                                    : 'bg-gray-100'
                                }`}>
                                  <Icon className={`w-5 h-5 ${
                                    isActive ? 'text-yellow-600' : 'text-gray-600'
                                  }`} />
                                </div>
                                <div className="text-left">
                                  <p className={`font-medium text-sm ${
                                    isActive ? 'text-yellow-900' : 'text-gray-900'
                                  }`}>
                                    {service.name}
                                  </p>
                                  {!service.active && (
                                    <p className="text-xs text-gray-500">Coming Soon</p>
                                  )}
                                </div>
                              </div>
                              {isActive && (
                                <CheckCircle className="w-5 h-5 text-yellow-600" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Notifications */}
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading dashboard...</p>
              </div>
            </div>
          ) : error ? (
            <div className="max-w-md mx-auto mt-12">
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center shadow-sm">
                <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Failed to Load</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={fetchDashboardData}
                  className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto">
              {/* Welcome Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome back, {fullName || 'Student'}
                </h2>
                <p className="text-gray-600">
                  Here's what's happening with your overseas education application
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-yellow-600" />
                    </div>
                    <span className="text-3xl font-bold text-gray-900">{progressPercent}%</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Overall Progress</h3>
                  <p className="text-sm text-gray-600">{completedStages} of {totalStages} stages completed</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className="text-3xl font-bold text-gray-900">{docStats.uploaded}/{docStats.total}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Documents</h3>
                  <p className="text-sm text-gray-600">Documents uploaded</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Current Stage</h3>
                  <p className="text-sm text-gray-600">{stageDisplay}</p>
                </div>
              </div>

              {/* Next Action */}
              {nextAction && (
                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-yellow-500 flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1">Next Step</h3>
                      <p className="text-gray-700 mb-4">{actionDisplay}</p>
                      <button
                        onClick={() => navigate('/dashboard/application')}
                        className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors inline-flex items-center space-x-2"
                      >
                        <span>Continue Application</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Application Timeline */}
                {applicationStages && applicationStages.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-bold text-gray-900">Recent Stages</h3>
                      <Link
                        to="/dashboard/progress"
                        className="flex items-center space-x-1 text-yellow-600 hover:text-yellow-700 text-sm font-medium"
                      >
                        <span>View All</span>
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>

                    <div className="space-y-4">
                      {applicationStages.slice(0, 4).map((stage, idx) => {
                        const isCompleted = stage.status === 'Completed';
                        const isInProgress = stage.status === 'In Progress';

                        return (
                          <div key={idx} className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-green-100' : isInProgress ? 'bg-yellow-100' : 'bg-gray-100'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : isInProgress ? (
                                <Clock className="w-4 h-4 text-yellow-600" />
                              ) : (
                                <span className="text-gray-500 text-xs font-semibold">{idx + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${
                                isCompleted ? 'text-green-700' : isInProgress ? 'text-yellow-700' : 'text-gray-600'
                              }`}>
                                {stage.title}
                              </p>
                              {stage.notes && (
                                <p className="text-gray-500 text-xs mt-0.5">{stage.notes}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-6">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate('/dashboard/uk/pathfinder')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <Globe className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-900">Find Universities</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>

                    <button
                      onClick={() => navigate('/dashboard/application')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <Upload className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-900">Submit Documents</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>

                    <button
                      onClick={() => navigate('/dashboard/progress')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <Eye className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-900">Track Progress</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>

                    <div className="pt-3 border-t border-gray-200">
                      <a
                        href="mailto:info.educential@gmail.com"
                        className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">Need Help?</p>
                            <p className="text-blue-600 text-xs">Contact Support</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-blue-400" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
